import { useMemo, useCallback } from 'react'
import { Table, Thead, Tbody, Tr, Td, Th, TableScrollHost } from '@/common/ui/Table'
import { Card } from '@/common/ui/Card/Card'
import type { ProductCardLlmBlockTypes } from '@/common/api/client/types.gen'
import {
    BLOCK_PROGRESS_COLORS,
    exportDetailedComparisonToExcel,
    getBlockDisplayValue,
    getOverallDisplayValue,
    type DetailedBrandComparisonRow,
    type DetailedComparisonFormat,
    type ExcelColumn,
} from '../../lib/detailedBrandComparison'
import type { CompanyStatsComparisonMode } from '../../model/useCompanyStatsBrandComparison'
import { DASHBOARD_BLOCK_TYPES, formatScoreValue } from '../../lib/scoreColors'
import { getBlockTitle } from '../../lib/labels'
import { CompanyStatsExcelExportButton } from '../CompanyStatsExcelExportButton'
import { DetailedBrandComparisonBlockProgressCell } from './DetailedBrandComparisonCells'

type CompanyStatsDetailedBrandComparisonBlocksTableProps = {
    compareBy: CompanyStatsComparisonMode
    rows: DetailedBrandComparisonRow[]
    format: DetailedComparisonFormat
    isLoading?: boolean
}

const findBlock = (row: DetailedBrandComparisonRow, blockType: ProductCardLlmBlockTypes) =>
    row.blocks.find(block => block.blockType === blockType)

export const CompanyStatsDetailedBrandComparisonBlocksTable = ({
    compareBy,
    rows,
    format,
    isLoading = false,
}: CompanyStatsDetailedBrandComparisonBlocksTableProps) => {
    const emptySelectionLabel = compareBy === 'brand' ? 'один бренд' : 'одного продавца'
    const blockMeta = useMemo(() => {
        const sampleRow = rows[0]
        if (!sampleRow) return []

        return DASHBOARD_BLOCK_TYPES.map(blockType => {
            const block = findBlock(sampleRow, blockType)
            return {
                blockType,
                title: getBlockTitle(blockType, block?.title),
                maxScore: block?.maxScore ?? 0,
            }
        }).filter(meta => meta.maxScore > 0 || rows.some(row => findBlock(row, meta.blockType)))
    }, [rows])

    const handleExport = useCallback(() => {
        const columns: ExcelColumn[] = [
            { header: '#', getValue: (_row, index) => index + 1 },
            { header: 'Бренд', getValue: row => row.name },
            { header: 'Товаров', getValue: row => row.productCount },
            {
                header: 'Общая',
                getValue: row => {
                    const { display } = getOverallDisplayValue(row, format)
                    return display
                },
            },
            ...blockMeta.map(meta => ({
                header: meta.title,
                getValue: (row: DetailedBrandComparisonRow) => {
                    const block = findBlock(row, meta.blockType)
                    if (!block) return '—'
                    return getBlockDisplayValue(block, format).display
                },
            })),
        ]
        exportDetailedComparisonToExcel(rows, columns, 'brand_comparison_blocks')
    }, [blockMeta, format, rows])

    return (
        <Card color='primary' tableScroll>
            <div className='mb-4 flex items-center justify-between gap-4'>
                <h3 className='text-xs font-bold uppercase tracking-wide text-white'>Сводная таблица по блокам</h3>
                <CompanyStatsExcelExportButton
                    onClick={handleExport}
                    disabled={rows.length === 0}
                    ariaLabel='Экспорт в Excel'
                />
            </div>

            <TableScrollHost minHeight='8rem'>
                <Table pinRows striped thBg rounded={false} className='w-full min-w-[56rem]'>
                    <Thead>
                        <Tr>
                            {[
                                <Th key='index' className='w-10'>
                                    #
                                </Th>,
                                <Th key='brand'>Бренд</Th>,
                                <Th key='products' className='text-center'>
                                    Товаров
                                </Th>,
                                <Th key='overall'>
                                    <span className='flex flex-col gap-0.5'>
                                        <span>Общая</span>
                                        <span className='font-normal normal-case text-base-content/50'>
                                            / {rows[0] ? formatScoreValue(rows[0].overallMaxScore) : '10'}
                                        </span>
                                    </span>
                                </Th>,
                                ...blockMeta.map(meta => (
                                    <Th key={meta.blockType}>
                                        <span className='flex flex-col gap-0.5'>
                                            <span>{meta.title}</span>
                                            <span className='font-normal normal-case text-base-content/50'>
                                                / {formatScoreValue(meta.maxScore)}
                                            </span>
                                        </span>
                                    </Th>
                                )),
                            ]}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading && rows.length === 0 ? (
                            <Tr>
                                <Td colSpan={4 + blockMeta.length} className='py-8 text-center text-base-content/70'>
                                    <span className='loading loading-spinner loading-md' aria-hidden />
                                </Td>
                            </Tr>
                        ) : rows.length === 0 ? (
                            <Tr>
                                <Td colSpan={4 + blockMeta.length} className='py-8 text-center text-base-content/60'>
                                    {`Выберите хотя бы ${emptySelectionLabel}`}
                                </Td>
                            </Tr>
                        ) : (
                            rows.map((row, index) => {
                                const overall = getOverallDisplayValue(row, format)
                                return (
                                    <Tr key={row.id}>
                                        {[
                                            <Td key='index' className='tabular-nums text-base-content/60'>
                                                {index + 1}
                                            </Td>,
                                            <Td key='brand'>
                                                <span className='inline-flex items-center gap-2'>
                                                    <span
                                                        className='inline-block h-2 w-2 shrink-0 rounded-full'
                                                        style={{ backgroundColor: row.color }}
                                                        aria-hidden
                                                    />
                                                    <span className='text-sm'>{row.name}</span>
                                                </span>
                                            </Td>,
                                            <Td key='products' className='text-center tabular-nums'>
                                                {row.productCount}
                                            </Td>,
                                            <Td key='overall'>
                                                <DetailedBrandComparisonBlockProgressCell
                                                    percent={overall.progressPercent}
                                                    display={overall.display}
                                                    color='#8b5cf6'
                                                    label={`${row.name}, общая оценка`}
                                                />
                                            </Td>,
                                            ...blockMeta.map(meta => {
                                                const block = findBlock(row, meta.blockType)
                                                if (!block) {
                                                    return (
                                                        <Td key={meta.blockType} className='text-base-content/40'>
                                                            —
                                                        </Td>
                                                    )
                                                }
                                                const value = getBlockDisplayValue(block, format)
                                                return (
                                                    <Td key={meta.blockType}>
                                                        <DetailedBrandComparisonBlockProgressCell
                                                            percent={value.progressPercent}
                                                            display={value.display}
                                                            color={BLOCK_PROGRESS_COLORS[meta.blockType]}
                                                            label={`${row.name}, ${meta.title}`}
                                                        />
                                                    </Td>
                                                )
                                            }),
                                        ]}
                                    </Tr>
                                )
                            })
                        )}
                    </Tbody>
                </Table>
            </TableScrollHost>
        </Card>
    )
}
